import React, { useState, useEffect, useRef } from "react";
import Navbar from "../navbar/Navbar";
import { useNavigate } from "react-router-dom";
import Loader from "../loader/Loader";
import axios from "axios";
import { API_URL } from "../../constant/Constant";
import { jwtDecode } from "jwt-decode";
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";
import * as FileSaver from "file-saver";
import { LuSearchX } from "react-icons/lu";
import toast from "react-hot-toast";

const UserList = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loader, setLoader] = useState(false);
  const [errors, setErrors] = useState({});
  const [data, setData] = useState([]);
  const [rows, setRows] = useState([]);
  const [managerFilter, setManagerFilter] = useState("0");
  const [managerOptions, setManagerOptions] = useState([]);
  const userId = localStorage.getItem("userId") || "";
  const userRoleId = localStorage.getItem("roleId") || "";
  const [userRole, setUserRole] = useState("");
  const [contextMenu, setContextMenu] = useState({
    show: false,
    x: 0,
    y: 0,
    userId: null,
  });
  const contextMenuRef = useRef(null);
  const tableRef = useRef(null);
  const navigate = useNavigate();

  // Format date as YYYY-MM-DD
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = ("0" + (date.getMonth() + 1)).slice(-2);
    const day = ("0" + date.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
  };

  // Close context menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        contextMenuRef.current &&
        !contextMenuRef.current.contains(event.target)
      ) {
        setContextMenu({ show: false, x: 0, y: 0, userId: null });
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle native context menu event
  useEffect(() => {
    const handleTableContextMenu = (event) => {
      event.preventDefault();
      console.log("Native contextmenu event:", {
        target: event.target.tagName,
        className: event.target.className,
      });
      const rowElement = event.target.closest("div.rdt_TableRow");
      if (rowElement) {
        // Try multiple attributes to identify the row
        const rowId =
          rowElement.getAttribute("data-id") ||
          rowElement.getAttribute("id")?.replace("row-", "") ||
          rowElement.getAttribute("data-row-id");
        console.log("Row element attributes:", {
          rowId,
          attributes: Object.fromEntries(
            [...rowElement.attributes].map((attr) => [attr.name, attr.value])
          ),
        });
        const rowData = data.find((row) => row.id.toString() === rowId);
        console.log("Row element found:", { rowId, rowData });
        if (rowData && Number(rowData.failedAttempts) >= 3) {
          console.log("Valid row for context menu:", rowData);
          const x = event.clientX;
          const y = event.clientY;
          const menuWidth = 200;
          const menuHeight = 50;
          const windowWidth = window.innerWidth;
          const windowHeight = window.innerHeight;
          const adjustedX =
            x + menuWidth > windowWidth ? windowWidth - menuWidth - 10 : x;
          const adjustedY =
            y + menuHeight > windowHeight ? windowHeight - menuHeight - 10 : y;
          console.log("Showing context menu:", {
            userId: rowData.id,
            x: adjustedX,
            y: adjustedY,
          });
          setContextMenu({
            show: true,
            x: adjustedX,
            y: adjustedY,
            userId: rowData.id,
          });
        } else {
          console.log("Invalid row or failedAttempts < 3:", {
            rowId,
            failedAttempts: rowData?.failedAttempts,
          });
        }
      } else {
        console.log("No row element found for contextmenu");
      }
    };

    const table = tableRef.current;
    if (table) {
      table.addEventListener("contextmenu", handleTableContextMenu);
    }
    return () => {
      if (table) {
        table.removeEventListener("contextmenu", handleTableContextMenu);
      }
    };
  }, [data]);

  // Fetch manager list
  const fetchManagers = async () => {
    const token = localStorage.getItem("accesstoken");
    if (!token) {
      localStorage.clear();
      navigate("/login");
      return;
    }
    try {
      const response = await axios.get(
        `${API_URL}api/v1/getDropdownOptionsForManagerAndAgents`
      );
      const managers = response.data.managers || [];
      setManagerOptions([
        { managerId: "0", managerName: "All" },
        ...managers.map((manager) => ({
          managerId: manager.managerId,
          managerName: manager.managerName || "N/A",
        })),
      ]);
    } catch (error) {
      console.error("Error fetching manager list:", error);
      setErrors((prev) => ({
        ...prev,
        fetchError: "Failed to load manager list.",
      }));
      setManagerOptions([{ managerId: "0", managerName: "All" }]);
    }
  };const handleResetFailedAttempts = async () => {
    if (!contextMenu.userId) return;
    setLoader(true);
    try {
      const token = localStorage.getItem("accesstoken");
      const response = await axios.post(
        `${API_URL}api/v1/updateFailedAttempts/${contextMenu.userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 200) {
        toast.success("Failed attempts reset successfully!");
        setTimeout(() => {
          window.location.reload();
        }, 2000); // Delay reload to allow toast to show
      }
    } catch (error) {
      console.error("Error resetting failed attempts:", error);
      const errorMessage = error.response?.data?.failedReason || "Failed to reset attempts.";
      setErrors((prev) => ({ ...prev, fetchError: errorMessage }));
      toast.error(errorMessage);
    } finally {
      setLoader(false);
      setContextMenu({ show: false, x: 0, y: 0, userId: null });
    }
  };

  // DataTable columns
  const columns = [
    {
      name: "User ID",
      selector: (row) => row.id,
      sortable: true,
      wrap: true,
      minWidth: "25px",
    },
    {
      name: "Name",
      selector: (row) => row.name || "N/A",
      sortable: true,
      wrap: true,
      minWidth: "100px",
    },
    {
      name: "Email",
      selector: (row) => row.email || "N/A",
      sortable: true,
      wrap: true,
      minWidth: "300px",
    },
    {
      name: "Active",
      selector: (row) => (row.active ? "Yes" : "No"),
      sortable: true,
      wrap: true,
      minWidth: "50px",
    },
    {
      name: "Role",
      selector: (row) => (row.role?.roleName ? row.role.roleName : "N/A"),
      sortable: true,
      wrap: true,
      minWidth: "200px",
    },
    {
      name: "Manager",
      selector: (row) => (row.managerName ? row.managerName : "N/A"),
      sortable: true,
      wrap: true,
      minWidth: "200px",
    },
    {
      name: "Product Access",
      selector: (row) =>
        row.productAccessOptionName ? row.productAccessOptionName : "N/A",
      sortable: true,
      wrap: true,
      minWidth: "100px",
    },
    {
      name: "Location",
      selector: (row) => (row.location ? row.location : "N/A"),
      sortable: true,
      wrap: true,
      minWidth: "100px",
    },
    {
      name: "Created Date",
      selector: (row) => (row.createdOn ? row.createdOn : "N/A"),
      sortable: true,
      wrap: true,
      minWidth: "100px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleEdit(row)}
            className="cta-one px-2 py-1 rounded-lg text-sm"
          >
            Edit
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      minWidth: "100px",
    },
  ];

  // Set default date range, fetch user role and managers
  useEffect(() => {
    const today = new Date();
    const formattedEndDate = formatDate(today);
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const formattedStartDate = formatDate(startOfMonth);

    setStartDate(formattedStartDate);
    setEndDate(formattedEndDate);
    setManagerFilter("0");

    const token = localStorage.getItem("accesstoken");
    if (token) {
      try {
        const tokenData = localStorage.getItem("roleId");
        setUserRole(tokenData || "");
        if (tokenData === "ROLE_COMPANY_DEALER") {
          fetchManagers();
        }
      } catch (error) {
        console.log("Error decoding token:", error);
        localStorage.clear();
        navigate("/login");
      }
    }

    fetchData(formattedStartDate, formattedEndDate, userId, userRoleId, "0");
  }, []);

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    if (name === "startDate") setStartDate(value);
    if (name === "endDate") setEndDate(value);
  };

  const handleManagerChange = (e) => {
    const selectedManager = e.target.value;
    setManagerFilter(selectedManager);
    console.log("Selected managerId:", selectedManager);
  };

  const fetchData = async (
    startDate,
    endDate,
    userId,
    userRoleId,
    managerId
  ) => {
    if (startDate > endDate) {
      setErrors((prev) => ({
        ...prev,
        fetchError: "Please select a valid date range",
      }));
      setData([]);
      setRows([]);
      return;
    }

    const token = localStorage.getItem("accesstoken");
    if (!token) {
      localStorage.clear();
      navigate("/login");
      return;
    }

    const tokenData = jwtDecode(token);
    const exp = tokenData.exp;
    const currentTime = Math.floor(Date.now() / 1000);
    if (exp < currentTime) {
      localStorage.clear();
      navigate("/login");
      return;
    }

    setLoader(true);
    try {
      const url = `${API_URL}api/v1/userList/${startDate}/${endDate}/${userId}/${userRoleId}/${managerId}`;
      console.log("Fetching user list with URL:", url);
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        const list = response.data || [];
        console.log("API response:", list);
        setData(list);
        setRows(list);
        setErrors((prev) => ({ ...prev, fetchError: "" }));
      }
    } catch (error) {
      console.log("Error fetching user list:", error);
      let errorMessage =
        error.response?.data?.failedReason || "Unknown error occurred.";
      setErrors((prev) => ({ ...prev, fetchError: errorMessage }));
      setData([]);
      setRows([]);
    } finally {
      setLoader(false);
    }
  };

  const handleEdit = async (row) => {
    setLoader(true);
    try {
      const token = localStorage.getItem("accesstoken");
      const url = `${API_URL}api/v1/editUser/${row.id}/${row.role.roleName}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        const userData = response.data;
        const currentRole = row.role.roleName;
        if (currentRole === "ROLE_MANAGER") {
          navigate("/editManager", { state: { user: userData || row } });
        } else if (currentRole === "ROLE_AGENT") {
          navigate("/editAgent", { state: { user: userData || row } });
        } else {
          navigate("/edit-user", { state: { user: userData || row } });
        }
      }
    } catch (error) {
      console.log("Edit API Error:", error);
      setErrors((prev) => ({
        ...prev,
        fetchError:
          error.response?.data?.failedReason || "Failed to initiate edit.",
      }));
    } finally {
      setLoader(false);
    }
  };

  const handleSearch = (e) => {
    const searchTerm = e.target.value.toString().toLowerCase();
    const newRows = rows.filter((row) => {
      const fields = [
        row.id?.toString() || "",
        row.email?.toString().toLowerCase() || "",
        row.username?.toString().toLowerCase() || "",
        row.active?.toString().toLowerCase() || "",
        row.role?.roleName?.toString().toLowerCase() || "",
        row.productAccessOptionName?.toString().toLowerCase() || "",
        row.location?.toString().toLowerCase() || "",
        row.failedAttempts?.toString() || "",
      ];
      return fields.some((field) => field.includes(searchTerm));
    });
    setData(newRows);
  };

  const exportToExcel = (data) => {
    const transformedData = data.map((user) => ({
      "User ID": user.id || "",
      Email: user.email || "N/A",
      Active: user.active ? "Yes" : "No",
      Role: user.role?.roleName || "N/A",
      "Product Access": user.productAccessOptionName || "N/A",
      Location: user.location || "N/A",
      "Failed Attempts": user.failedAttempts ?? "0",
    }));

    const worksheet = XLSX.utils.json_to_sheet(transformedData);
    const workbook = { Sheets: { Users: worksheet }, SheetNames: ["Users"] };
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const date = new Date();
    const formattedDate = `${date.getFullYear()}-${String(
      date.getMonth() + 1
    ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    const filename = `User Data ${formattedDate}.xlsx`;
    const dataBlob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    FileSaver.saveAs(dataBlob, filename);
  };

  const conditionalRowStyles = [
    {
      when: (row) => !row.active,
      style: { backgroundColor: "transparent" },
    },
    {
      when: (row) => row.active,
      style: { backgroundColor: "transparent" },
    },
    {
      when: (row) => Number(row.failedAttempts) >= 3,
      style: { backgroundColor: "#fff3cd" },
    },
  ];

  return (
    <>
      <Navbar />
      <div className="mt-3">
        {errors?.fetchError && (
          <p className="error-text text-center">{errors.fetchError}</p>
        )}
        <main className="my-order-table mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between border-b border-gray-200 py-6">
            <h1 className="text-2xl font-semibold tracking-tight mb-3">
              User List
            </h1>
            <div className="flex flex-wrap gap-y-4 items-center space-x-1">
              {userRole === "ROLE_COMPANY_DEALER" && (
                <div className="flex items-center space-x-2">
                  <label className="whitespace-nowrap">Manager</label>
                  <div className="relative w-48">
                    <select
                      name="managerFilter"
                      value={managerFilter}
                      onChange={handleManagerChange}
                      className="rounded-lg bg-transparent border custom-border-50 focus:border-2 focus:custom-border-50 placeholder:text-teal-800/50 placeholder:text-sm w-full h-10 appearance-none pl-3 pr-8"
                    >
                      {managerOptions.map((manager) => (
                        <option
                          key={manager.managerId}
                          value={manager.managerId}
                        >
                          {manager.managerName}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-600">
                      ▼
                    </div>
                  </div>
                </div>
              )}
              <input
                className="rounded-lg bg-transparent border custom-border-50 focus:border-2 focus:custom-border-50 placeholder:text-teal-800/50 placeholder:text-sm w-44 h-10"
                type="date"
                name="startDate"
                value={startDate}
                onChange={handleDateChange}
              />
              <input
                className="rounded-lg bg-transparent border custom-border-50 focus:border-2 focus:custom-border-50 placeholder:text-teal-800/50 placeholder:text-sm w-44 h-10"
                type="date"
                name="endDate"
                value={endDate}
                onChange={handleDateChange}
              />
              <button
                onClick={() =>
                  fetchData(
                    startDate,
                    endDate,
                    userId,
                    userRoleId,
                    managerFilter
                  )
                }
                className="cta-one px-2 py-2 w-1/15 rounded-lg"
              >
                Go
              </button>
              {/* {rows?.length > 0 && (
                <button
                  onClick={() => exportToExcel(rows)}
                  className="cta-one px-3 py-2 w-1/10 rounded-lg"
                >
                  Export to Excel
                </button>
              )} */}
              <input
                type="search"
                className="rounded-lg bg-transparent border custom-border-50 focus:border-2 focus:custom-border-50 placeholder:text-teal-800/50 placeholder:text-sm w-44 h-10"
                placeholder="Search"
                onChange={handleSearch}
              />
            </div>
          </div>
          {data.length > 0 ? (
            <div ref={tableRef}>
              <DataTable
                columns={columns}
                data={data}
                conditionalRowStyles={conditionalRowStyles}
                fixedHeader
                pagination
              />
            </div>
          ) : (
            <div className="w-full min-h-screen flex flex-col items-center mt-24">
              <LuSearchX className="text-5xl mb-2" />
              <p>No users found</p>
            </div>
          )}
          {contextMenu.show && (
            <div
              ref={contextMenuRef}
              style={{
                position: "fixed",
                top: contextMenu.y,
                left: contextMenu.x,
                background: "#fff",
                border: "1px solid #ccc",
                borderRadius: "4px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                zIndex: 9999,
                width: "200px",
              }}
            >
              <div
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  background: "#fff",
                  color: "#333",
                }}
                onMouseEnter={(e) => (e.target.style.background = "#f0f0f0")}
                onMouseLeave={(e) => (e.target.style.background = "#fff")}
                onClick={handleResetFailedAttempts}
              >
                Reset Failed Attempts
              </div>
            </div>
          )}
        </main>
      </div>
      {loader && <Loader />}
      <style jsx>{`
        .rdt_TableRow {
          pointer-events: auto !important;
        }
        .rdt_TableCell {
          pointer-events: auto !important;
        }
      `}</style>
    </>
  );
};

export default UserList;
